import { CountryCode, FlagComponent } from '@commenda/kakashi';
import { getCountryName, getPrintableCorporationType } from '@commenda/utils';
import { Country, CorporationType } from '@prisma/client';
import { ReactNode, useState } from 'react';
import { ENTITY_NODE_HEIGHT, ENTITY_NODE_WIDTH } from '../graph/layout';

// Inline hex so values survive consuming app's Tailwind content-scanner.
const PILL_BG_REST = '#FFFFFF';
const PILL_BG_HOVER = '#F8F6F1';
// External / non-controlled entities: tinted bg + dashed border so they read
// as "outside the portfolio" even at zoom-out where opacity alone disappears.
const PILL_BG_EXTERNAL = '#F2EFE8';
const PILL_BORDER = '#DEDBD4';

interface EntityPillProps {
  legalName: string;
  incorporationCountry: Country | null;
  businessEntityType: CorporationType | null;
  dashed?: boolean;
  // Non-controlled / external entities: muted to 50% opacity and non-interactive.
  disabled?: boolean;
  onClick?: () => void;
  // UnlinkedEntitiesPanel mirrors canvas zoom by scaling the inner pill via
  // CSS transform; outer box sizes to scaled dims so flex sees the scaled bbox.
  scale?: number;
  // ReactFlow <Handle>s are absolutely positioned against the pill bbox and
  // must render inside this div.
  children?: ReactNode;
}

const EntityPill = ({
  legalName,
  incorporationCountry,
  businessEntityType,
  dashed = false,
  disabled = false,
  onClick,
  scale = 1,
  children,
}: EntityPillProps) => {
  const countryName = getCountryName(incorporationCountry);
  // Skip badge without country: getPrintableCorporationType uses country for
  // jurisdiction-specific overrides (e.g. "Limited Company" vs "Ltd").
  const typeLabel =
    businessEntityType && incorporationCountry
      ? getPrintableCorporationType(businessEntityType, incorporationCountry)
      : null;

  // Mirrors apps/enterprise/components/HomeScreen/EntityCard.tsx: in-progress
  // incorporations don't have a `legalName` yet, so fall back to a country-
  // tagged stub. Country code (e.g. "US") not full name — short and scannable.
  const hasLegalName = Boolean(legalName);
  const displayName =
    hasLegalName || !incorporationCountry
      ? legalName
      : `New ${incorporationCountry} Entity`;

  // State-driven hover (not Tailwind hover:) keeps bg in inline-style scope so
  // Tailwind preflight can't out-style it.
  const [hovered, setHovered] = useState(false);

  const interactive = Boolean(onClick) && !disabled;
  const scaled = scale !== 1;

  const pill = (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={disabled ? undefined : onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      className="flex items-center gap-lg rounded-full border transition-colors"
      style={{
        width: ENTITY_NODE_WIDTH,
        height: ENTITY_NODE_HEIGHT,
        paddingTop: 8,
        paddingBottom: 10,
        paddingLeft: 12,
        paddingRight: 12,
        borderColor: PILL_BORDER,
        // Externals get dashed border + tinted bg + 75% opacity (instead of
        // 50% opacity-only) so they're distinguishable at zoom-out without
        // disappearing into the canvas. The shared `dashed` prop also dashes
        // unlinked-panel pills for the same "not part of the tree" reading.
        borderStyle: dashed || disabled ? 'dashed' : 'solid',
        backgroundColor: disabled
          ? PILL_BG_EXTERNAL
          : hovered
            ? PILL_BG_HOVER
            : PILL_BG_REST,
        cursor: interactive ? 'pointer' : undefined,
        opacity: disabled ? 0.75 : undefined,
        transform: scaled ? `scale(${scale})` : undefined,
        transformOrigin: scaled ? 'top left' : undefined,
      }}
    >
      {children}

      <span className="size-9 shrink-0 overflow-hidden rounded-full">
        {incorporationCountry && (
          <FlagComponent
            country={incorporationCountry as CountryCode}
            countryName={countryName ?? ''}
            _className="!size-9"
          />
        )}
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-xxs">
        {/* No truncation tooltip — the EntityNode popover surfaces the full
            legalName already, so a second hover surface would race + double-fire. */}
        <p
          className={
            hasLegalName
              ? 'block min-w-0 truncate font-bold text-text-primary'
              : 'block min-w-0 truncate font-medium text-text-disabled'
          }
          style={{ fontSize: 14, lineHeight: '20px' }}
        >
          {displayName}
        </p>
        {typeLabel && (
          <div className="flex min-w-0 max-w-full items-center gap-sm self-start">
            <span
              className="min-w-0 truncate rounded-xs bg-utility-gray-50 px-sm py-xxs font-medium text-utility-gray-800"
              style={{ fontSize: 12, lineHeight: '16px' }}
            >
              {typeLabel}
            </span>
          </div>
        )}
      </div>
    </div>
  );

  // Native size: return pill directly so flex/handle geometry matches what
  // ReactFlow expects. Scaled: wrap in sized box so flex sees scaled bbox.
  if (!scaled) return pill;
  return (
    <div
      style={{
        width: ENTITY_NODE_WIDTH * scale,
        height: ENTITY_NODE_HEIGHT * scale,
      }}
    >
      {pill}
    </div>
  );
};

export { EntityPill };
